import React from 'react';
import Link from '@fogia/core-web/components/Link';
import { useTranslation } from 'react-i18next';
import Image from '@fogia/core-web/components/Image';
import tracking from '@fogia/core-web/components/Tracking';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import Events from '@fogia/core-web/libs/Events';
import { ProductEvents } from '@fogia/core-web/libs/Events/constants';
import { generateSrcSet } from '@fogia/core-web/libs/image';
import Text from 'components/Text';
import { isWideProduct } from 'Utils/isWideProduct';
import styled from 'libs/styled';
import { useHistory } from 'react-router-dom';

const filterTransientProps = { shouldForwardProp: (prop: string) => !prop.startsWith('$') };

const CardWrapper = styled('div')``;

const CardLink = styled(Link)`
    display: block;
`;

const ImageContainer = styled('div', filterTransientProps)<{ $transitionHeight?: boolean }>`
    position: relative;
    height: var(--product-image-height, auto);
    overflow: hidden;
    background-color: var(--ui-surface);
    ${({ $transitionHeight }) => ($transitionHeight ? `transition: height var(--transition-slider);` : '')}
`;

const Badge = styled('span')`
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 1;
    background-color: var(--ui-background);
    padding: 5px 10px;
    color: var(--ui-primary-aa);
`;

const ImageWrapper = styled('div')`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }
`;

const ProductInfo = styled('div')`
    display: flex;
    flex-direction: column;
    margin-top: 7px;
`;

export interface ProductCardProps extends Record<string, unknown> {
    product: ProductCardProduct;
    gridColumn?: string;
    /** Reserve vertical space under the image */
    productInfoMinHeight?: string;
    /** Animate image area height when --product-image-height changes (e.g. product slider active state) */
    transitionImageHeight?: boolean;
}

export type ProductCardProduct = {
    categories: Array<string>;
    family: string;
    image: string;
    imageKey: string;
    imagery: Array<string>;
    manufacturer: { name: string };
    name: string;
    objectID: string;
    price: number;
    pricing: any;
    productId: number;
    slug: string;
    stock: number;
    thumbnail: string;
    uniqueName: string;
    url: string;
    variantColors?: Array<{ name: string; color: string }>;
    packShotSize?: string;
    badge?: string;
    designer?: string;
};

export const ProductCard = ({
    product,
    gridColumn = '',
    productInfoMinHeight,
    transitionImageHeight = false,
    ...rest
}: ProductCardProps) => {
    const { t } = useTranslation();
    const { slug, thumbnail, imagery } = product;

    const productUrl = `${slug}`;
    const currencyCode = useAppSelector(({ application }) => application?.site?.currency?.code) || '';
    const history = useHistory();
    const widths = [140, 304, 336, 400, 480, 560, 720, 960];

    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            Events.trigger(ProductEvents.CLICK, {
                eventId: tracking.getUniqueId(),
                product: {
                    item_id: product.objectID,
                    item_name: product.name,
                    price: product.price,
                    item_brand: product.manufacturer?.name || '',
                    item_variant: product.objectID || '',
                    quantity: '',
                },
                currency: currencyCode,
            });
        } catch (e) {
            //
        }
        history.push(slug);
    };

    const imageSrc = thumbnail || '';
    const imagerySrc = thumbnail || imagery?.[0] || '';
    const srcSet = generateSrcSet(imageSrc, widths);

    const isWide = isWideProduct(product);

    // Use the gridColumn prop if provided, otherwise set based on whether the product is wide.
    const computedGridColumn = gridColumn || `span ${isWide ? '2' : '1'}`;

    return (
        <CardWrapper {...rest} className={isWide ? 'wide-product-card' : ''} gridColumn={computedGridColumn}>
            <CardLink to={productUrl} onClick={handleClick}>
                <ImageContainer $transitionHeight={transitionImageHeight}>
                    {product.badge && (
                        <Badge>
                            <Text typography="Primary/12_120_510_-1">{product.badge}</Text>
                        </Badge>
                    )}
                    <ImageWrapper>
                        {imagerySrc ? (
                            <Image src={{ url: imagerySrc, width: widths }} srcSet={srcSet} alt={product.name} />
                        ) : (
                            <Text typography="Primary/15_120_510_-1">{t('image_not_available')}</Text>
                        )}
                    </ImageWrapper>
                </ImageContainer>

                <ProductInfo minHeight={productInfoMinHeight || ''}>
                    <Text typography="Primary/15_120_510_-1">{product.name}</Text>
                    <Text typography="Secondary/15_120_400">{product.designer || product.manufacturer?.name}</Text>
                </ProductInfo>
            </CardLink>
        </CardWrapper>
    );
};
